import cx from "classnames";
import React, { useEffect, useId, useState } from "react";

import { ButtonType, HeroButton } from "@gaming/xds-react-js";

import { clarityMaskProps } from "../../clarity";
import { useStrings } from "../../localization/localizationHelpers";
import { AddressValidationAddress } from "../../server/partnerCenter/models/addressValidation";
import { Modal, ModalInitialFocus } from "../Modal/Modal";
import * as styles from "./AddressSuggestionModal.module.css";

type AddressSuggestionModalProps = Readonly<{
  isOpen: boolean;
  suggestedAddresses: AddressValidationAddress[];
  originalAddress: AddressValidationAddress;
  onConfirm: (address: AddressValidationAddress) => void;
  onReject: () => void;
  darkMode?: boolean;
}>;

const formatAddressLines = (address: AddressValidationAddress): string[] => {
  const lines: string[] = [];
  if (address.address_line1) {
    lines.push(address.address_line1);
  }
  if (address.address_line2) {
    lines.push(address.address_line2);
  }
  if (address.address_line3) {
    lines.push(address.address_line3);
  }

  const cityRegionPostal = [address.city, address.region, address.postal_code]
    .filter(Boolean)
    .join(", ");
  if (cityRegionPostal) {
    lines.push(cityRegionPostal);
  }
  if (address.country) {
    lines.push(address.country);
  }

  return lines;
};

export const AddressSuggestionModal = ({
  isOpen,
  suggestedAddresses,
  originalAddress,
  onConfirm,
  onReject,
  darkMode,
}: AddressSuggestionModalProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const titleId = useId();
  const [
    addressSuggestionTitle,
    addressSuggestionBody,
    addressSuggestionCancel,
    addressSuggestionContinue,
    addressSuggestionOriginalLabel,
    addressSuggestionVerified,
    addressSuggestionUnverified,
  ] = useStrings(
    "addressSuggestionTitle",
    "addressSuggestionBody",
    "addressSuggestionCancel",
    "addressSuggestionContinue",
    "addressSuggestionOriginalLabel",
    "addressSuggestionVerified",
    "addressSuggestionUnverified"
  );
  // All options: suggested addresses + original address as last
  const allAddresses = [...suggestedAddresses, originalAddress];

  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen, suggestedAddresses]);

  const handleConfirm = () => {
    const selected = allAddresses[selectedIndex];
    if (selected) {
      onConfirm(selected);
    }
  };

  const handleAddressKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      setSelectedIndex(index);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onReject}
      ariaLabelledBy={titleId}
      initialFocus={ModalInitialFocus.FirstFocusable}
      className={styles.dialog}
    >
      <div className={cx(styles.modalBody, darkMode && styles.modalBodyDark)}>
        <h2 id={titleId} className={cx(styles.title, darkMode && styles.titleDark)}>
          {addressSuggestionTitle}
        </h2>
        <p className={cx(styles.body, darkMode && styles.bodyDark)}>{addressSuggestionBody}</p>
        <fieldset className={styles.cardsContainer} aria-labelledby={titleId} role="radiogroup">
          {allAddresses.map((address, index) => {
            const lines = formatAddressLines(address);
            const isSelected = index === selectedIndex;
            const isOriginal = index === allAddresses.length - 1;
            const addressKey = `${address.address_line1}-${address.city}-${address.postal_code}-${
              isOriginal ? "original" : index
            }`;

            return (
              <label
                key={addressKey}
                className={cx(
                  styles.addressCard,
                  darkMode && styles.addressCardDark,
                  isSelected && styles.addressCardSelected,
                  isSelected && darkMode && styles.addressCardSelectedDark
                )}
              >
                <input
                  type="radio"
                  className={styles.radioInput}
                  id={`${titleId}-address-${index}`}
                  name={`${titleId}-address`}
                  checked={isSelected}
                  onChange={() => setSelectedIndex(index)}
                  onKeyDown={event => handleAddressKeyDown(event, index)}
                />
                <div className={styles.addressDetails}>
                  {isOriginal ? (
                    <p className={styles.unverifiedBadge}>
                      {addressSuggestionUnverified}
                      <span
                        className={cx(styles.originalLabel, darkMode && styles.originalLabelDark)}
                      >
                        {` ${addressSuggestionOriginalLabel}`}
                      </span>
                    </p>
                  ) : (
                    <p className={styles.verifiedBadge}>{addressSuggestionVerified}</p>
                  )}
                  {/* Street addresses are rendered as read-only text, which Clarity
                      does not mask automatically the way it masks form inputs. */}
                  <div {...clarityMaskProps}>
                    {lines.map(line => (
                      <p
                        key={line}
                        className={cx(styles.addressLine, darkMode && styles.addressLineDark)}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </label>
            );
          })}
        </fieldset>
        <div className={styles.buttonRow}>
          {darkMode ? (
            <>
              <button
                type="button"
                className={cx(styles.cancelButton, styles.cancelButtonDark)}
                onClick={onReject}
              >
                {addressSuggestionCancel}
              </button>
              <button
                type="button"
                className={cx(styles.confirmButton, styles.confirmButtonDark)}
                onClick={handleConfirm}
              >
                {addressSuggestionContinue}
              </button>
            </>
          ) : (
            <>
              <HeroButton
                as="button"
                buttonType={ButtonType.Secondary}
                className={styles.cancelButton}
                onClick={onReject}
              >
                {addressSuggestionCancel}
              </HeroButton>
              <HeroButton as="button" className={styles.confirmButton} onClick={handleConfirm}>
                {addressSuggestionContinue}
              </HeroButton>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
